import { dayFill, dayNeedsALook, formatMinutes } from '@kronos/domain'
import type { DayLine } from '@kronos/domain'

/**
 * What one square of the hours calendar says: the time worked, a bar filled
 * against an eight-hour day, "On shift" while the clock is running, and "Fix"
 * on a day with a forgotten clock-out or work signed off with no shift.
 */
export function HoursCell({ day }: { day: DayLine | undefined }): JSX.Element | null {
  if (!day) return null
  const fill = dayFill(day.minutes)
  return (
    <>
      {day.shifts.some((s) => s.onShift) ? <span className="hours-cell__on">On shift</span> : null}
      {dayNeedsALook(day) ? <span className="hours-cell__fix">Fix</span> : null}
      {day.shifts.some((s) => !s.forgotten) ? <span className="hours-cell__time">{formatMinutes(day.minutes)}</span> : null}
      {day.minutes > 0 ? (
        <span className="hours-cell__bar">
          <span style={{ width: `${Math.round(fill * 100)}%` }} />
        </span>
      ) : null}
    </>
  )
}

/** The square's state, as classes: worked (tinted by how full), or needing a look. */
export function hoursDayClass(day: DayLine | undefined): string {
  if (!day) return ''
  if (dayNeedsALook(day)) return 'hours--look'
  return day.minutes > 0 ? `hours--worked hours--tint-${Math.max(1, Math.ceil(dayFill(day.minutes) * 4))}` : ''
}

/** The spoken half: "7h 50m worked, on shift, needs fixing". */
export function describeHours(day: DayLine | undefined): string {
  if (!day || day.shifts.length + day.work.length === 0) return 'nothing clocked'
  return [
    day.shifts.length > 0 ? `${formatMinutes(day.minutes)} worked` : '',
    day.shifts.some((s) => s.onShift) ? 'on shift' : '',
    dayNeedsALook(day) ? 'needs fixing' : '',
  ]
    .filter(Boolean)
    .join(', ')
}
